/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LinkedDeploymentType } from 'src/clu/appShell/cluDeployModelPage/components/OrchestrationDeployModelModal';
import { CluIntentStore, CluOrchestrationInfo, LuisOrchestrationInfo } from 'src/clu/core/stores/CluIntentStore';
import { LuisDropdown, LuisDropdownProps } from 'src/core/components/LuisDropdown';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';

type ConnectedIntentDeployConfigsProps = Partial<LuisDropdownProps> & {
    deploymentName: string;
    intent: CluIntentStore;
    onDeploymentConfigsChange: (deploymentType: LinkedDeploymentType, deploymentName: string) => void;
};

const messages = defineMessages({
    placeholder: { id: 'clu.connectedIntentDeployConfigs.placeholder', defaultMessage: 'Select' },
    stagingSlot: { id: 'clu.connectedIntentDeployConfigs.stagingSlot', defaultMessage: 'Staging - Slot deployment' },
    versionDeployment: { id: 'clu.connectedIntentDeployConfigs.versionDeployment', defaultMessage: 'Version deployment' },
    productionSlot: { id: 'clu.connectedIntentDeployConfigs.productionSlot', defaultMessage: 'Production - Slot deployment' }
});

export const ConnectedIntentDeployConfigs = observer((props: ConnectedIntentDeployConfigsProps) => {
    const { deploymentName, intent, isLoading, onDeploymentConfigsChange, ...rest } = props;
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const luisAppVersionTracker = useProgressTracker();

    const cluApp = rootStore.cluProjectStoreHouse.findById((intent.orchestrationInfo as CluOrchestrationInfo).projectName);
    const luisApp = rootStore.luisAppStoreHouse.findById((intent.orchestrationInfo as LuisOrchestrationInfo).appId);

    React.useEffect(() => {
        switch (intent.orchestrationType) {
            case 'Luis':
                dispatcher.dispatch('loadAppVersions', {
                    tracker: luisAppVersionTracker,
                    context: { appId: (intent.orchestrationInfo as LuisOrchestrationInfo).appId }
                });
                break;
            case 'Conversation':
                dispatcher.dispatch('getCluDeployments', { projectName: cluApp.name, deploymentsStoreHouse: cluApp.deploymentsStoreHouse });
                break;
            default:
        }
    }, [intent.orchestrationType]);

    const luisDeploymentsOptions: IDropdownOption[] = [
        { key: 'staging', text: localize(messages.stagingSlot), data: { deploymentType: 'slot' } },
        { key: 'production', text: localize(messages.productionSlot), data: { deploymentType: 'slot' } },
        ...(luisApp?.appVersionStoreHouse.items.map(version => ({
            key: version.id,
            data: { deploymentType: 'version' },
            text: `v${version.id} - ${localize(messages.versionDeployment)}`
        })) || [])
    ];
    const cluDeploymentsOptions: IDropdownOption[] =
        cluApp?.deploymentsStoreHouse.items.map(d => ({
            key: d.deploymentName,
            text: d.deploymentName,
            data: { deploymentType: 'clu' }
        })) || [];

    return (
        <LuisDropdown
            selectedKey={deploymentName}
            styles={{ root: { width: '100%' } }}
            placeholder={localize(messages.placeholder)}
            disabled={intent.orchestrationType === 'QuestionAnswering'}
            options={intent.orchestrationType === 'Conversation' ? cluDeploymentsOptions : luisDeploymentsOptions}
            onChange={(_, option) => onDeploymentConfigsChange(option.data.deploymentType, option.key as string)}
            isLoading={luisAppVersionTracker.isRunning || cluApp?.deploymentsStoreHouse.tracker.isRunning || isLoading}
            {...rest}
        />
    );
});
